import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography, Alert, Chip, Divider } from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { selectCMSSelectedPilot } from 'store/dataReducers/cmsPilotSlice';
import { MigrationJob } from 'types/apis/migrationInterfaces';
import { MigrationUploadForm } from './components/MigrationUploadForm';
import { MigrationJobDetails } from './components/MigrationJobDetails';

type ViewMode = 'upload' | 'details';

/**
 * Reco Dry Run — standalone authenticated page (/cms/reco-dry-run).
 *
 * Reuses the migration upload form + job-details components, but runs the
 * migration endpoint in `dryRun` mode: sheets are parsed, transformed, and
 * validated against the live CMS catalog, and a report is produced WITHOUT
 * importing anything. Uses the globally-selected CMS pilot (same as the other
 * /cms pages), so a pilot must be selected first.
 */
export const RecoDryRunPage: React.FC = () => {
  const selectedPilot = useSelector(selectCMSSelectedPilot);
  const [viewMode, setViewMode] = useState<ViewMode>('upload');
  const [selectedJob, setSelectedJob] = useState<MigrationJob | null>(null);

  const pilotId = selectedPilot?.pilotId as string | undefined;
  const supportedLocales = Array.isArray(selectedPilot?.supportedLocales)
    ? (selectedPilot?.supportedLocales as string[])
    : undefined;
  const pilotLabel = (selectedPilot as { name?: string } | undefined)?.name || pilotId;

  const handleStarted = (job: MigrationJob) => {
    setSelectedJob(job);
    setViewMode('details');
  };

  const handleBack = () => {
    setSelectedJob(null);
    setViewMode('upload');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <ScienceOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Reco Dry Run
        </Typography>
        {pilotId && <Chip size="small" label={`Pilot: ${pilotLabel}`} sx={{ ml: 1 }} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Validate recommendation sheets against the CMS catalog before importing. Reports category
        mappings, <code>others</code> fallbacks, and blocking issues (missing appliances / home-profile
        attributes). Nothing is written to the CMS.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {!pilotId ? (
        <Alert severity="info">
          Select a CMS pilot first (from the pilot switcher) to run a dry-run analysis.
        </Alert>
      ) : (
        <Paper variant="outlined">
          {viewMode === 'upload' && (
            <MigrationUploadForm
              dryRun
              pilotId={pilotId}
              supportedLocales={supportedLocales}
              onMigrationStarted={handleStarted}
            />
          )}
          {viewMode === 'details' && selectedJob && (
            <MigrationJobDetails
              key={selectedJob.jobId}
              job={selectedJob}
              onBack={handleBack}
              backLabel="Run another dry run"
              pollIntervalMs={2500}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RecoDryRunPage;
